import React, { useState, useEffect, useRef } from 'react';
import { TextField, Grid, FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormContainer from '../../components/FormContainer';
import { addCast, updateCast, fetchCastCategories } from '../../utils/api';

const CastForm = ({ editCast = null, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isEditMode = !!editCast;
  const onSuccessTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (onSuccessTimerRef.current != null) {
        clearTimeout(onSuccessTimerRef.current);
        onSuccessTimerRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      try {
        const response = await fetchCastCategories();
        if (cancelled) return;
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching caste categories:', error);
        if (!cancelled) {
          setErrorMessage('Failed to load caste categories. Please refresh the page.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadCategories();
    return () => {
      cancelled = true;
    };
  }, []);

  const validationSchema = Yup.object({
    castName: Yup.string().required('Caste name is required').max(100, 'Caste name must be at most 100 characters'),
    description: Yup.string().required('Description is required').max(500, 'Description must be at most 500 characters'),
    categoryId: Yup.number().required('Category is required'),
  });

  const formik = useFormik({
    initialValues: {
      castName: editCast ? editCast.castName : '',
      description: editCast ? editCast.description : '',
      categoryId: editCast ? editCast.category.categoryId : '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      setIsSubmitting(true);
      setSuccessMessage(null);
      setErrorMessage(null);

      try {
        const castData = {
          castName: values.castName,
          description: values.description,
          category: {
            categoryId: values.categoryId
          }
        };
        
        if (isEditMode) {
          await updateCast(editCast.castId, castData, {
            previousData: editCast,
            identifier: editCast.castName || values.castName
          });
          setSuccessMessage('Caste updated successfully!');
        } else {
          await addCast(castData, {
            identifier: values.castName
          });
          setSuccessMessage('Caste added successfully!');
          resetForm();
        }

        if (onSuccess) {
          if (onSuccessTimerRef.current != null) clearTimeout(onSuccessTimerRef.current);
          onSuccessTimerRef.current = setTimeout(() => {
            onSuccessTimerRef.current = null;
            onSuccess();
          }, 1500);
        }
      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'adding'} caste:`, error);
        setErrorMessage(error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'add'} caste. Please try again.`);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const handleCloseSnackbar = () => {
    setSuccessMessage(null);
    setErrorMessage(null);
  };

  if (isLoading) {
    return <div>Loading categories...</div>;
  }

  return (
    <FormContainer
      title={isEditMode ? "Edit Caste" : "Add Caste"}
      onSubmit={formik.handleSubmit}
      isSubmitting={isSubmitting}
      successMessage={successMessage}
      errorMessage={errorMessage}
      onCloseSnackbar={handleCloseSnackbar}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl 
            fullWidth 
            margin="normal"
            error={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
          >
            <InputLabel id="category-select-label">Category</InputLabel>
            <Select
              labelId="category-select-label"
              id="categoryId"
              name="categoryId"
              value={formik.values.categoryId}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Category"
            >
              {categories.map((category) => (
                <MenuItem key={category.categoryId} value={category.categoryId}>
                  {category.categoryName}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.categoryId && formik.errors.categoryId && (
              <FormHelperText>{formik.errors.categoryId}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="castName"
            name="castName"
            label="Caste Name"
            value={formik.values.castName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.castName && Boolean(formik.errors.castName)}
            helperText={formik.touched.castName && formik.errors.castName}
            margin="normal"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="description"
            name="description"
            label="Description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            margin="normal"
            variant="outlined"
            multiline
            rows={4}
          />
        </Grid>
      </Grid>
    </FormContainer>
  );
};

export default CastForm; 